import React from "react";
import FileUpload from "./FileUpload";
import {
  Box,
  Button,
  Collapse,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import "image-viewer/dist/singleImage/singleImage.iife.min.js";
import {InputOutlined} from "@mui/icons-material";
import defaultImage from "../resources/default-image.jpg";
import {SessionContext} from "./SessionContext";
import {FixedSizeList as VirtualizedList} from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";

const MOSTLY_ALPHANUMERIC = /^[a-zA-Z0-9\-,:;?]$/;
const RECORD_NUMBER_PATTERN = /REC\d+/;

function Row({index, style}) {
  const {selectedElementRef, list, setCurrentImageName, currentEntry, setCurrentEntry, setIndex} = React.useContext(SessionContext);
  const entry = list[index];
  const handleListItemClick = (nineFive) => {
    setCurrentImageName(nineFive);
  };

  const currentlySelected = currentEntry === entry;

  const handleOnItemClick = () => {
    handleListItemClick(entry);
    setCurrentEntry(entry);
    setIndex(index);
  };

  const selectedItemStyles = {
    border: "#097ad7",
    borderWidth: 2,
    borderStyle: "solid",
    borderRadius: 1
  };

  return (
    <ListItem key={index} style={style}>
      <ListItemButton
        sx={currentlySelected ? selectedItemStyles : {}}
        onClick={handleOnItemClick}
        ref={currentlySelected ? selectedElementRef : null}
      >
        <ListItemText primary={entry} secondary={index + 1} primaryTypographyProps={{fontSize: "0.8rem"}}/>
      </ListItemButton>
    </ListItem>
  );
}

function ResultsList() {
  const {index, list} = React.useContext(SessionContext);
  const itemCount = list.length;
  const positionText = `${index + 1} of ${itemCount}`;

  return (
    <Stack height={"100%"}>
      <Typography align={"center"} sx={{height: 24, backgroundColor: "#efefef"}}>{positionText}</Typography>
      <AutoSizer>
        {({height, width}) => (
          <VirtualizedList height={height - 24} width={width} itemCount={itemCount} itemSize={55}>
            {Row}
          </VirtualizedList>
        )}
      </AutoSizer>
    </Stack>
  );
}

function Body({currentImageName, labels, setLabels, labelsTextFieldRef}) {
  const {listLoaded, sessionId} = React.useContext(SessionContext);
  const viewerRef = React.useRef(null);
  const imageName = getImageName(currentImageName);
  const source = listLoaded ? `https://www.familysearch.org/dz/v1/dgs:${imageName}/image.xml?ctx=&session=${sessionId}` : defaultImage;

  const [textFieldFocused, setTextFieldFocused] = React.useState(false);

  const handleTextFieldFocus = () => {
    setTextFieldFocused(true);
  };
  const handleTextFieldBlur = () => {
    setTextFieldFocused(false);
  };
  const handleKeyDown = (event) => {
    if (textFieldFocused) {
      event.stopPropagation();
    }
  };

  const handleLabelsUpdate = event => {
    const newValue = event.target.value;
    localStorage.setItem("imageLabelerLabels", newValue);
    setLabels(newValue);
  };

  if (viewerRef && viewerRef.current) {
    viewerRef.current.listeningToKeyboard = false;  // Prevent keyboard shortcuts from affecting viewer
  }

  return (
    <Box flex={1} overflow={"hidden"} paddingBottom={1}>
      <Stack direction={"row"} flex={1} display={"flex"} flexDirection={"row"} height={"100%"} overflow={"hidden"}>
        {listLoaded && <Box width={280}><ResultsList/></Box>}
        <Box flex={1} display={"flex"} flexDirection={"column"}>
          {listLoaded && <Typography align={"center"} sx={{backgroundColor: "#efefef"}}>{imageName}</Typography>}
          <fs-single-image-viewer ref={viewerRef} src={source}/>
        </Box>
        {listLoaded && <Box width={400} padding={1} overflow={"auto"} ref={labelsTextFieldRef}>
          <TextField value={labels} onChange={handleLabelsUpdate} multiline fullWidth
                     onFocus={handleTextFieldFocus}
                     onBlur={handleTextFieldBlur}
                     onKeyDown={handleKeyDown}
                     sx={{minWidth: "20%", overflowY: "auto"}}
          />
        </Box>}
      </Stack>
    </Box>
  );
}

function InputBar({sessionId, setSessionId, onFileUpload}) {
  return (
    <Paper sx={{margin: 1}} variant={"outlined"}>
      <Stack spacing={4} direction={"row"} alignItems={"center"} padding={2} sx={{backgroundColor: "#f8faff"}}>
        <Box width={300}>
          <TextField
            value={sessionId}
            label={"Prod Session Id"}
            required={true}
            onChange={event => {
              const newValue = event.target.value;
              setSessionId(newValue);
              sessionStorage.setItem("sessionId", newValue);
            }}
            fullWidth
          />
        </Box>
        <FileUpload onChange={onFileUpload} allowedExtensions={[".txt"]}/>
      </Stack>
    </Paper>
  );
}

function LabelerView() {
  const [currentEntry, setCurrentEntry] = React.useState("");
  const currentRecordNumber = currentEntry.match(RECORD_NUMBER_PATTERN) ? currentEntry.match(RECORD_NUMBER_PATTERN)[0] : "";
  const [list, setList] = React.useState([]);
  const [index, setIndex] = React.useState(0);
  const [currentImageName, setCurrentImageName] = React.useState(list.length > 0 ? list[index] : "");  // The nineFive of the image currently in view
  const cachedLabels = localStorage.getItem("imageLabelerLabels") ? localStorage.getItem("imageLabelerLabels") : "";
  const [labels, setLabels] = React.useState(cachedLabels);
  const [currentLabel, setCurrentLabel] = React.useState("");
  const cachedSessionId = sessionStorage.getItem("sessionId") ? sessionStorage.getItem("sessionId") : "";
  const [sessionId, setSessionId] = React.useState(cachedSessionId);
  const [showInputs, setShowInputs] = React.useState(true);
  const [downloadFilename, setDownloadFilename] = React.useState("ace-image-labeler-data");

  const selectedElementRef = React.useRef();
  const labelsTextFieldRef = React.useRef();

  const buttonLabel = showInputs ? "Hide Inputs" : "Show Inputs";
  const listLoaded = list.length > 0;

  const [textFieldFocused, setTextFieldFocused] = React.useState(false);
  const handleTextFieldFocus = () => {
    setTextFieldFocused(true);
  };
  const handleTextFieldBlur = () => {
    setTextFieldFocused(false);
  };
  const handleKeyDown = (event) => {
    const keyCode = event.key;
    if (textFieldFocused && keyCode !== "Enter") {
      event.stopPropagation();
    }
  };

  const handleKeyPress = React.useCallback((event) => {
    const keyCode = event.key;
    if (!listLoaded) {
      console.log("No list loaded");
      return;
    }
    if (keyCode === "ArrowLeft" && !event.shiftKey) {
      const imageName = getImageName(currentImageName);
      const nine = imageName.split("_")[0];
      let five = imageName.split("_")[1];
      five = String(parseInt(five) - 1).padStart(5, "0");
      const previousNineFive = `${nine}_${five}`;
      setCurrentImageName(previousNineFive);
    }
    if (keyCode === "ArrowRight" && !event.shiftKey) {
      const imageName = getImageName(currentImageName);
      const nine = imageName.split("_")[0];
      let five = imageName.split("_")[1];
      five = String(parseInt(five) + 1).padStart(5, "0");
      const nextNineFive = `${nine}_${five}`;
      setCurrentImageName(nextNineFive);
    }
    if (keyCode === "ArrowUp" && !event.shiftKey) {
      if (index > 0) {
        const previousIndex = index - 1;
        setIndex(previousIndex);
        const previousEntry = list[previousIndex];
        setCurrentEntry(previousEntry);
        setCurrentImageName(getImageName(previousEntry));
      }
    }
    if (keyCode === "ArrowDown" && !event.shiftKey) {
      toNextEntry(index, list, setIndex, setCurrentEntry, setCurrentImageName);
    }

    if (MOSTLY_ALPHANUMERIC.test(keyCode) && !(event.metaKey || event.ctrlKey)) {
      setCurrentLabel(() => `${currentLabel}${keyCode}`);
    }
    if (keyCode === "Enter") {
      const newValue = `${labels}\n${currentEntry}\t${currentLabel}`.trim();
      localStorage.setItem("imageLabelerLabels", newValue);
      setLabels(newValue);
      setCurrentLabel("");
      toNextEntry(index, list, setIndex, setCurrentEntry, setCurrentImageName);
      if (labelsTextFieldRef.current) {
        labelsTextFieldRef.current.scrollTop = labelsTextFieldRef.current.scrollHeight;
      }
    }
    if (keyCode === "Escape") {
      setCurrentLabel("");
    }
    if (keyCode === "Backspace") {
      setCurrentLabel(removeLastChar(currentLabel));
    }
  }, [listLoaded, currentImageName, index, list, currentLabel, labels, currentEntry]);

  React.useEffect(() => {
    if (selectedElementRef.current) {
      selectedElementRef.current.scrollIntoView({behavior: "smooth", block: "center"});
    }
  }, [index]);

  React.useEffect(() => {
    document.addEventListener("keydown", handleKeyPress);
    return () => {
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [handleKeyPress]);

  async function onFileUpload(file) {
    const updatedFilename = `${file[0].fileObj.name.replace(".txt", "")}-labels`;
    setDownloadFilename(updatedFilename);
    const text = await file[0].fileObj.text();
    const list = text.split("\n").filter(x => x);
    setList(list);
    if (list.length > 0) {
      const firstEntry = list[0];
      setCurrentImageName(getImageName(firstEntry));
      setCurrentEntry(firstEntry);
    }
    sessionStorage.setItem("sessionId", sessionId);
    setShowInputs(false);
  }

  const providerData = {
    sessionId: sessionId,
    listLoaded: listLoaded,
    selectedElementRef: selectedElementRef,
    list: list,
    currentImageName: currentImageName,
    setCurrentImageName: setCurrentImageName,
    currentEntry: currentEntry,
    setCurrentEntry: setCurrentEntry,
    index: index,
    setIndex: setIndex
  };

  return (<SessionContext.Provider value={providerData}>
      <Box height={"100vh"} overflow={"auto"} display={"flex"} flexDirection={"column"}>
        <Collapse in={showInputs}>
          <InputBar sessionId={sessionId} setSessionId={setSessionId} onFileUpload={onFileUpload}/>
        </Collapse>
        <Stack direction={"row"} spacing={2} alignItems={"center"} padding={1} paddingTop={2}>
          <Button startIcon={<InputOutlined/>} onClick={() => setShowInputs(!showInputs)} sx={{height: 56}}>{buttonLabel}</Button>
          <Box flex={1}/>
          {currentRecordNumber !== "" && <Paper sx={{paddingX: 2}}>
            <ListItemText primary={currentRecordNumber} secondary={"Current Record"}/>
          </Paper>}
          <Box width={300}>
            <TextField label={"Label Value"} value={currentLabel} fullWidth
                       onChange={event => setCurrentLabel(event.target.value)}
                       onFocus={handleTextFieldFocus}
                       onBlur={handleTextFieldBlur}
                       onKeyDown={handleKeyDown}
            />
          </Box>
          <Stack direction={"row"} alignItems={"center"}>
            <Box width={300}>
              <TextField label={"Download Filename"} value={downloadFilename} fullWidth
                         onChange={event => setDownloadFilename(event.target.value)}
                         onFocus={handleTextFieldFocus}
                         onBlur={handleTextFieldBlur}
                         onKeyDown={handleKeyDown}
              />
            </Box>
            <Typography>.txt</Typography>
          </Stack>
          <Button variant="contained"
                  color="secondary"
                  onClick={() => downloadData(new Blob([labels], {type: "plain/text"}), downloadFilename)}
                  sx={{height: 56}}
          >
            Download Labels
          </Button>
        </Stack>
        <Body
          currentImageName={currentImageName}
          labels={labels}
          setLabels={setLabels}
          labelsTextFieldRef={labelsTextFieldRef}
        />
      </Box>
    </SessionContext.Provider>)
}

function getImageName(input) {
  if (!input) {
    return "";
  }
  return input.substring(0, 15);
}

function toNextEntry(index, list, setIndex, setCurrentEntry, setCurrentImageName) {
  if (index < list.length - 1) {
    const nextIndex = index + 1;
    setIndex(nextIndex);
    const nextEntry = list[nextIndex];
    setCurrentEntry(nextEntry);
    setCurrentImageName(getImageName(nextEntry));
  }
}

function removeLastChar(inputString) {
  if (inputString.length === 0) {
    return "";
  } else {
    return inputString.slice(0, -1);
  }
}

function downloadData(blob, filename) {
  const objectURL = window.URL.createObjectURL(blob);
  const downloadLink = document.createElement("a");
  downloadLink.setAttribute("download", filename + ".txt");
  downloadLink.href = objectURL;
  document.body.appendChild(downloadLink);

  window.requestAnimationFrame(() => {
    downloadLink.dispatchEvent(new MouseEvent("click"));
    document.body.removeChild(downloadLink);
  });
  localStorage.setItem("imageLabelerLabels", "");
}

export default LabelerView;
